import { useState, FC, useEffect } from 'react';
import {
  Box,
  Input,
  Text,
  Textarea,
  Flex,
  Portal,
  Dialog,
  Tabs,
  Checkbox,
  RadioGroup
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { CustomButton } from './UI/CustomButton';
import { MdAttachFile, MdCancel, MdFileUpload } from 'react-icons/md';
import { useToastContext } from './UI/ToastManager';
import { API_URL } from '../utils/graphqlRequest';
import { uploadVideoMultipart } from '../query/useUploadVideoMutation';

interface ValueChangeDetails<T = string> {
  value: T;
}

interface AddExerciseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exercise?: ExerciseData | null; // null for add, object for edit
  mode?: 'add' | 'edit';
}

interface ExerciseData {
  id?: string;
  name: string;
  backgroundVideo: {
    duration: string;
    source: string;
    title: string;
  };
  description: { language: string; text: string }[];
  displayName: { language: string; text: string }[];
  poster: {
    source: string;
  };
  sideChange: boolean;
  videos: { language: string; video: { duration?: string; height?: string; source?: string; title?: string; width?: string } }[];
}

const languages = ['fr', 'en', 'it', 'de', 'nl', 'es'];

// Helper to convert hh:mm:ss to seconds
function durationToSeconds(duration: string): number | null {
  if (!duration) return null;
  const parts = duration.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 1) {
    return parts[0];
  }
  return null;
}

// Helper to convert seconds to hh:mm:ss
function secondsToDuration(seconds: number | null): string {
  if (!seconds) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Helper to remove empty fields from objects recursively
function removeEmptyFields(obj: any): any {
  if (obj === null || obj === undefined) {
    return undefined;
  }
  
  if (Array.isArray(obj)) {
    const filteredArray = obj
      .map(item => removeEmptyFields(item))
      .filter(item => item !== undefined && item !== null);
    return filteredArray.length > 0 ? filteredArray : undefined;
  }
  
  if (typeof obj === 'object') {
    const result: any = {};
    let hasNonEmptyFields = false;
    
    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = removeEmptyFields(value);
      if (cleanedValue !== undefined && cleanedValue !== null && cleanedValue !== '') {
        result[key] = cleanedValue;
        hasNonEmptyFields = true;
      }
    }
    
    return hasNonEmptyFields ? result : undefined;
  }
  
  // For primitive values, return as is (empty strings will be filtered out above)
  return obj;
}

const initialExerciseData: ExerciseData = {
  name: '',
  backgroundVideo: {
    duration: '',
    source: '',
    title: ''
  },
  description: languages.map(lang => ({ language: lang, text: '' })),
  displayName: languages.map(lang => ({ language: lang, text: '' })),
  poster: {
    source: ''
  },
  sideChange: false,
  videos: languages.map(lang => ({ language: lang, video: {} })),
};

export const AddExerciseDialog: FC<AddExerciseDialogProps> = ({ open, onOpenChange, exercise, mode = 'add' }) => {
  const { showSuccess, showError } = useToastContext();
  const queryClient = useQueryClient();
  const [videoType, setVideoType] = useState<'upload' | 'url'>('upload');
  const [posterType, setPosterType] = useState<'upload' | 'url'>('url');
  const [langTab, setLangTab] = useState('en');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [exerciseData, setExerciseData] = useState<ExerciseData>(initialExerciseData);
  const [isUploading, setIsUploading] = useState(false);
  // 1. Update ExerciseData type and initialExerciseData to support 'source' in description
  const [translationVideoFiles, setTranslationVideoFiles] = useState<{ [lang: string]: File | null }>({});
  const [translationVideoTypes, setTranslationVideoTypes] = useState<{ [lang: string]: 'upload' | 'url' }>({});

  // Load exercise data when editing
  useEffect(() => {
    if (exercise && mode === 'edit') {
      // Ensure sideChange is properly converted to boolean and duration is formatted
      const exerciseWithProperSideChange = {
        ...exercise,
        sideChange: Boolean(exercise.sideChange),
        backgroundVideo: {
          ...exercise.backgroundVideo,
          duration: typeof exercise.backgroundVideo.duration === 'number' 
            ? secondsToDuration(exercise.backgroundVideo.duration)
            : exercise.backgroundVideo.duration
        },
        videos: exercise.videos.map(video => ({
          ...video,
          video: {
            ...video.video,
            duration: typeof video.video.duration === 'number'
              ? secondsToDuration(video.video.duration)
              : video.video.duration
          }
        }))
      };
      setExerciseData(exerciseWithProperSideChange);
      // Set video and poster types based on source (if it's a file path, assume upload)
      setVideoType(exercise.backgroundVideo.source ? 'url' : 'upload');
      setPosterType(exercise.poster.source ? 'url' : 'upload');
      
      // Set translation video types based on existing data
      const translationTypes: { [lang: string]: 'upload' | 'url' } = {};
      exercise.videos.forEach(video => {
        if (video.video.source) {
          translationTypes[video.language] = 'url';
        } else {
          translationTypes[video.language] = 'upload';
        }
      });
      setTranslationVideoTypes(translationTypes);
    } else {
      // Reset to initial state for add mode
      setExerciseData(initialExerciseData);
      setVideoType('upload');
      setPosterType('url');
      setLangTab('en');
      setVideoFile(null);
      setPosterFile(null);
      setTranslationVideoTypes({});
      setTranslationVideoFiles({});
    }
  }, [exercise, mode, open]);

  const updateExerciseField = (field: string, value: any) => {
    setExerciseData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = <K extends keyof ExerciseData, F extends keyof ExerciseData[K]>(
    section: K,
    field: F,
    value: any
  ) => {
    setExerciseData(prev => {
      const current = prev[section];
      if (typeof current !== 'object' || current === null || Array.isArray(current)) return prev;
      return {
        ...prev,
        [section]: { ...current, [field]: value },
      };
    });
  };

  const findTextDataByLanguage = (section: 'description' | 'displayName', language: string) => {
    return exerciseData[section].find(item => item.language === language);
  };

  const findVideoDataByLanguage = (language: string) => {
    return exerciseData.videos.find(item => item.language === language);
  };

  const updateTextDataByLanguage = (section: 'description' | 'displayName', language: string, value: string) => {
    setExerciseData(prev => {
      const arr = Array.from(prev[section]);
      const index = arr.findIndex(item => item.language === language);
      if (index !== -1) {
        // Update existing language entry
        arr[index] = { ...arr[index], text: value };
      } else {
        // Add new language entry
        arr.push({ language, text: value });
      }
      return { ...prev, [section]: arr };
    });
  };

  const updateVideoDataByLanguage = (language: string, field: keyof ExerciseData['videos'][0]['video'], value: any) => {
    setExerciseData(prev => {
      const arr = Array.from(prev.videos);
      const index = arr.findIndex(item => item.language === language);
      if (index !== -1) {
        // Update existing language entry
        arr[index] = { 
          ...arr[index], 
          video: { ...arr[index].video, [field]: value }
        };
      } else {
        // Add new language entry
        arr.push({ 
          language, 
          video: { [field]: value }
        });
      }
      return { ...prev, videos: arr };
    });
  };

  // 3. Add helpers for updating translation image fields
  const updateTranslationVideoType = (lang: string, type: 'upload' | 'url') => {
    setTranslationVideoTypes(prev => ({ ...prev, [lang]: type }));
  };
  const updateTranslationVideoFile = (lang: string, file: File | null) => {
    setTranslationVideoFiles(prev => ({ ...prev, [lang]: file }));
  };

  const handleDialogOpenChange = (open: boolean) => onOpenChange(open);
  const handleVideoTypeChange = (details: ValueChangeDetails<'upload' | 'url'>) => setVideoType(details.value);
  const handlePosterTypeChange = (details: ValueChangeDetails<'upload' | 'url'>) => setPosterType(details.value);
  const handleLangTabChange = (details: ValueChangeDetails<string>) => setLangTab(details.value);

  // Upload media files and return their URLs
  const uploadMedia = async (file: File, type: 'video' | 'image'): Promise<string | null> => {
    // Create the GraphQL query for upload
    const mutation = type === 'video' ? `
      mutation uploadVideo($video: Upload!, $videoType: String!) {
        uploadVideo(video: $video, videoType: $videoType) {
          success
          message
        }
      }
    ` : `
      mutation uploadImage($image: Upload!, $imageType: String!) {
        uploadImage(image: $image, imageType: $imageType) {
          success
          message
        }
      }
    `;

    // Create the operations map
    const operations = {
      query: mutation,
      variables: {
        [type === 'video' ? 'video' : 'image']: null,
        [type === 'video' ? 'videoType' : 'imageType']: type === 'video' ? 'exercise_video' : 'exercise_poster'
      }
    };

    // Create the file map
    const map = {
      [type === 'video' ? '0' : '0']: [`variables.${type === 'video' ? 'video' : 'image'}`]
    };

    const formData = new FormData();
    formData.append('operations', JSON.stringify(operations));
    formData.append('map', JSON.stringify(map));
    formData.append('0', file);

    const token = localStorage.getItem('authToken')

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.data?.uploadVideo?.success || result.data?.uploadImage?.success) {
        // The response should contain the uploaded file URL
        const uploadResult = result.data?.uploadVideo || result.data?.uploadImage;
        return uploadResult?.message || uploadResult?.url || uploadResult?.publicUrl;
      } else {
        const errorMessage = result.data?.uploadVideo?.message || result.data?.uploadImage?.message || 'Upload failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Upload error:', error);
      showError('Upload Failed', `Failed to upload ${type}. Please try again.`);
      return null;
    }
  };

  // 4. Update handleSaveExercise to upload translation images if needed
  const handleSaveExercise = async () => {
    setIsUploading(true);
    
    try {
      // First, upload any media files if needed
      let backgroundVideoUrl = exerciseData.backgroundVideo.source;
      let posterUrl = exerciseData.poster.source;
      let videos: {
        language: string;
        video: {
          source?: string;
          title?: string;
          duration?: string;
        };
      }[];
      // Upload background video if it's a file upload
      if (videoType === 'upload' && videoFile) {
        const uploadResult = await uploadVideoMultipart(videoFile, showError);

        if (!uploadResult) {
          setIsUploading(false);
          showError(`Failed to upload background video`);
          return;
        }

        const { publicUrl, posterUrl } = uploadResult;
        backgroundVideoUrl = publicUrl;
      }
      // Upload translated videos if it's a files to upload
      if (translationVideoFiles && Object.keys(translationVideoFiles).length > 0) {
        const updatedVideosRaw = await Promise.all(
          Object.entries(translationVideoFiles).map(async ([lang, file]) => {
            if (!file) {
              showError(`Missing video file for language: ${lang}`);
              return undefined;
            }

            const uploadResult = await uploadVideoMultipart(file, showError);

            if (!uploadResult) {
              showError(`Failed to upload video for language: ${lang}`);
              return undefined;
            }

            const { publicUrl, posterUrl } = uploadResult;
            const originalVideo = exerciseData.videos.find(
              v => v.language.toLowerCase() === lang.toLowerCase()
            );

            return {
              language: lang,
              video: {
                source: publicUrl,
                title: originalVideo?.video?.title,
                // posterSource: posterUrl,
                duration: originalVideo?.video?.duration || ''
              }
            };
          })
        );

        const updatedVideos = updatedVideosRaw.filter(
          (v): v is NonNullable<typeof v> => v !== undefined
        );

        const updatedLangs = new Set(updatedVideos.map(v => v.language.toLowerCase()));

        const preservedVideos = exerciseData.videos.filter(
          v => !updatedLangs.has(v.language.toLowerCase())
        );
        videos = [...updatedVideos, ...preservedVideos];
      } else {
        videos = exerciseData.videos;
      }

      // Upload poster if it's a file upload
      if (posterType === 'upload' && posterFile) {
        const uploadedPosterUrl = await uploadMedia(posterFile, 'image');
        if (!uploadedPosterUrl) {
          setIsUploading(false);
          return; // Stop if upload failed
        }
        posterUrl = uploadedPosterUrl;
      }

    const mutation = mode === 'add' ? `
      mutation createExercise($exercise: ExerciseInput!) {
        createExercise(exercise: $exercise) {
          id
        }
      }
    ` : `
      mutation updateExercise($id: ID!, $exercise: ExerciseUpdateInput!) {
        updateExercise(id: $id, exercise: $exercise) {
          id
        }
      }
    `;

    const input: any = {
      name: exerciseData.name,
      backgroundVideo: {
        ...exerciseData.backgroundVideo,
        source: backgroundVideoUrl,
        duration: durationToSeconds(exerciseData.backgroundVideo.duration),
      },
      poster: {
        ...exerciseData.poster,
        source: posterUrl,
      },
      description: exerciseData.description
        .filter(d => d.text.trim())
        .map(d => ({ ...d, language: d.language.toLowerCase() })),
      displayName: exerciseData.displayName
        .filter(d => d.text.trim())
        .map(d => ({ ...d, language: d.language.toLowerCase() })),
      videos: (videos.length ? videos : exerciseData.videos)
        .filter(v => v.video && v.video.source)
        .map(v => ({
          ...v,
          language: v.language.toLowerCase(),
          video: {
            ...v.video,
            duration: durationToSeconds(v.video.duration || ''),
          },
        })),
      sideChange: exerciseData.sideChange,
    };

    // Remove empty fields from the input before sending
    const cleanedInput = removeEmptyFields(input);

      const variables = mode === 'add' ? { exercise: cleanedInput } : { id: exerciseData.id, exercise: cleanedInput };
      const token = localStorage.getItem('authToken');

      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            query: mutation,
            variables,
          }),
        });
        const result = await response.json();
        
        if (result.data?.createExercise?.id || result.data?.updateExercise?.id) {
          const action = mode === 'add' ? 'created' : 'updated';
          showSuccess(`Exercise ${action}`, `The exercise has been successfully ${action}.`);
          
          // Invalidate the exercises query cache to refresh the list
          queryClient.invalidateQueries({ queryKey: ['exercises'] });
          
          onOpenChange(false);
        } else {
          const action = mode === 'add' ? 'creating' : 'updating';
          showError(`${action.charAt(0).toUpperCase() + action.slice(1)} Failed`, `Failed to ${action} exercise. Please check your input and try again.`);
        }
      } catch (err) {
        const action = mode === 'add' ? 'creating' : 'updating';
        showError('Network Error', `An error occurred while ${action} the exercise. Please check your connection and try again.`);
      }
    } catch (err) {
      console.error('Save exercise error:', err);
      showError('Error', 'An error occurred while saving the exercise. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleDialogOpenChange} className="default-dialog">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
        <Dialog.Content minWidth="960px" padding='50px 64px'>
          <Dialog.Header p='0'>
            <Dialog.Title as="div" style={{ lineHeight: '36px' }}>
              {mode === 'add' ? 'Add New Exercise' : 'Edit Exercise'}
            </Dialog.Title>
            <Flex gap={3} ml="auto">
              <CustomButton onClick={() => onOpenChange(false)} visual="subtle" size="sm">Cancel</CustomButton>
              <CustomButton size="sm" onClick={handleSaveExercise} disabled={isUploading}>
                {isUploading ? 'Saving...' : (mode === 'add' ? 'Add Exercise' : 'Save Changes')}
              </CustomButton>
            </Flex>
          </Dialog.Header>

          <Dialog.Body p='0' mt='16px'>
            <Box>
              <Text fontWeight="medium" mb='8px'>Generic name</Text>
              <Input placeholder="Generic name" mb='24px'
                value={exerciseData.name}
                onChange={e => updateExerciseField('name', e.target.value)}
              />

              <Flex mb='24px'>
                <Text fontWeight="medium" mb={1} mr='32px'>Background Youtube Video:</Text>

                <Flex flexDirection="column" flex={1}>
                  <RadioGroup.Root colorPalette="pink" variant="outline" value={videoType} onValueChange={handleVideoTypeChange} mb='26px' display="flex" gap='48px'>
                    <RadioGroup.Item value="upload">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <span style={{ marginLeft: '8px' }}>Upload file</span>
                    </RadioGroup.Item>
                    <RadioGroup.Item value="url">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <span style={{ marginLeft: '8px' }}>Youtube URL</span>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                
                  {videoType === 'upload' ? (
                    <Flex align="center" gap={3} mb={2}>
                      {videoFile ? (
                        <CustomButton visual="ghost" h='48px' border="1px solid #D0CFCF" borderRadius="6px" onClick={() => {
                            setVideoFile(null);
                            updateNestedField('backgroundVideo', 'source', '');
                          }}>
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <MdCancel />
                          </Box>
                      </CustomButton>
                      ) : (
                        <CustomButton as="label" visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px'>
                          <Box display="flex" flexDirection="column" alignItems="center" fontWeight="medium" color="#8B8B8B" lineHeight="14px">
                            <MdFileUpload />
                            Upload
                          </Box>
                          <Input
                            type="file"
                            display="none"
                            accept="video/*"
                            onChange={e => {
                              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                              setVideoFile(file);
                              updateNestedField('backgroundVideo', 'source', file ? file.name : '');
                            }}
                          />
                        </CustomButton>
                      )}
                      <Box color="gray.500" fontSize="sm" display="flex" alignItems="center" gap={1}>
                        <MdAttachFile />
                        {videoFile ? videoFile.name : 'No file selected yet'}
                      </Box>
                    </Flex>
                  ) : (
                    <Input
                      placeholder="Youtube URL"
                      mb={2}
                      value={exerciseData.backgroundVideo.source}
                      onChange={e => updateNestedField('backgroundVideo', 'source', e.target.value)}
                    />
                  )}
                </Flex>
              </Flex>
            
              <Flex gap='24px' align="center" mb='24px'>
                <Box flex={1}>
                  <Text fontWeight="medium" mb='8px'>Background Youtube Video Title</Text>
                  <Input
                    placeholder="What you want the user to see in the app"
                    flex={1}
                    value={exerciseData.backgroundVideo.title}
                    onChange={e => updateNestedField('backgroundVideo', 'title', e.target.value)}
                  />
                </Box>
                <Box width="120px">
                  <Text fontWeight="medium" mb='8px'>Duration</Text>
                  <Input
                    placeholder="hh:mm:ss"
                    width="120px"
                    value={exerciseData.backgroundVideo.duration}
                    onChange={e => updateNestedField('backgroundVideo', 'duration', e.target.value)}
                  />
                </Box>
              </Flex>
              <Flex mb='24px'>
                <Text fontWeight="medium" mb={1} mr='32px'>Poster:</Text>
                <Flex flexDirection="column" flex={1}>
                  <RadioGroup.Root
                    colorPalette="pink"
                    variant="outline"
                    value={posterType}
                    onValueChange={handlePosterTypeChange}
                    mb='26px'
                    display="flex"
                    gap='48px'
                  >
                    <RadioGroup.Item value="upload">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <span style={{ marginLeft: '8px' }}>Upload file</span>
                    </RadioGroup.Item>
                    <RadioGroup.Item value="url">
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemIndicator />
                      <span style={{ marginLeft: '8px' }}>Image URL</span>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                  {posterType === 'upload' ? (
                    <Flex align="center" gap={3} mb={2}>
                      {posterFile ? (
                        <CustomButton visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px' onClick={() => {
                          setPosterFile(null);
                          updateNestedField('poster', 'source', '');
                        }}>
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <MdCancel />
                          </Box>
                        </CustomButton>
                      ) : (
                        <CustomButton as="label" visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px'>
                          <Box display="flex" flexDirection="column" alignItems="center" fontWeight="medium" color="#8B8B8B" lineHeight="14px">
                            <MdFileUpload />
                            Upload
                          </Box>
                          <Input
                            type="file"
                            display="none"
                            accept="image/*"
                            onChange={e => {
                              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                              setPosterFile(file);
                              updateNestedField('poster', 'source', file ? file.name : '');
                            }}
                          />
                        </CustomButton>
                      )}
                      <Box color="gray.500" fontSize="sm" display="flex" alignItems="center" gap={1}>
                        <MdAttachFile />
                        {posterFile ? posterFile.name : 'No file selected yet'}
                      </Box>
                    </Flex>
                  ) : (
                    <Input
                      placeholder="Image URL"
                      mb={2}
                      value={exerciseData.poster.source}
                      onChange={e => updateNestedField('poster', 'source', e.target.value)}
                    />
                  )}
                </Flex>
              </Flex>
              <Checkbox.Root 
                checked={exerciseData.sideChange} 
                onCheckedChange={(checked: any) => {
                  // Handle different possible values from Chakra UI v3
                  let booleanValue = false;
                  if (typeof checked === 'object' && checked !== null) {
                    booleanValue = checked.checked === true;
                  } else {
                    booleanValue = checked === true || checked === 'true' || checked === 1;
                  }
                  console.log('Converted to boolean:', booleanValue);
                  updateExerciseField('sideChange', booleanValue);
                }} 
                mb='36px'
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Checkbox.Label>Require side change</Checkbox.Label>
              </Checkbox.Root>

              <Box padding='16px' border='1px solid #DADADA' borderRadius='5px'>
                <Tabs.Root value={langTab} onValueChange={handleLangTabChange} variant="subtle" colorPalette="pink" mb={2}>
                  <Tabs.List mb='24px' gap='24px'>
                    {languages.map((lang: string, idx: number) => (
                      <Tabs.Trigger key={lang} value={lang}>
                        {lang}
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                  {languages.map((lang: string) => (
                    <Tabs.Content key={lang} value={lang}>
                      <Box style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <Text fontWeight="medium" mb='8px'>Display Name</Text>
                        <Input
                          placeholder="Display Name"
                          mb='24px'
                          value={findTextDataByLanguage('displayName', lang)?.text || ''}
                          onChange={e => updateTextDataByLanguage('displayName', lang, e.target.value)}
                        />
                        <Text fontWeight="medium" mb='8px'>Description</Text>
                        <Textarea
                          placeholder="Description"
                          mb='24px'
                          value={findTextDataByLanguage('description', lang)?.text || ''}
                          onChange={e => updateTextDataByLanguage('description', lang, e.target.value)}
                        />
                        {/* Youtube Video Upload - match background video UI */}
                        <Flex mb='24px'>
                          <Text fontWeight="medium" mb={1} mr='32px'>Youtube Video:</Text>
                          <Flex flexDirection="column" flex={1}>
                            <RadioGroup.Root colorPalette="pink" variant="outline" value={translationVideoTypes[lang] || 'url'} onValueChange={(details: ValueChangeDetails<'upload' | 'url'>) => updateTranslationVideoType(lang, details.value)} mb='26px' display="flex" gap='48px'>
                              <RadioGroup.Item value="upload">
                                <RadioGroup.ItemHiddenInput />
                                <RadioGroup.ItemIndicator />
                                <span style={{ marginLeft: '8px' }}>Upload file</span>
                              </RadioGroup.Item>
                              <RadioGroup.Item value="url">
                                <RadioGroup.ItemHiddenInput />
                                <RadioGroup.ItemIndicator />
                                <span style={{ marginLeft: '8px' }}>Youtube URL</span>
                              </RadioGroup.Item>
                            </RadioGroup.Root>
                            {translationVideoTypes[lang] === 'upload' ? (
                              <Flex align="center" gap={3} mb={2}>
                                {translationVideoFiles[lang] ? (
                                  <CustomButton visual="ghost" h='48px' border="1px solid #D0CFCF" borderRadius="6px" onClick={() => {
                                    updateTranslationVideoFile(lang, null);
                                    updateVideoDataByLanguage(lang, 'source', '');
                                  }}>
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                      <MdCancel />
                                    </Box>
                                  </CustomButton>
                                ) : (
                                  <CustomButton as="label" visual="ghost" border="1px solid #D0CFCF" borderRadius="6px" h='48px'>
                                    <Box display="flex" flexDirection="column" alignItems="center" fontWeight="medium" color="#8B8B8B" lineHeight="14px">
                                      <MdFileUpload />
                                      Upload
                                    </Box>
                                    <Input
                                      type="file"
                                      display="none"
                                      accept="video/*"
                                      onChange={e => {
                                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                        updateTranslationVideoFile(lang, file);
                                        updateVideoDataByLanguage(lang, 'source', file ? file.name : '');
                                      }}
                                    />
                                  </CustomButton>
                                )}
                                <Box color="gray.500" fontSize="sm" display="flex" alignItems="center" gap={1}>
                                  <MdAttachFile />
                                  {translationVideoFiles[lang] ? translationVideoFiles[lang]?.name : 'No file selected yet'}
                                </Box>
                              </Flex>
                            ) : (
                              <Input
                                placeholder="Youtube URL"
                                mb={2}
                                value={findVideoDataByLanguage(lang)?.video?.source || ''}
                                onChange={e => updateVideoDataByLanguage(lang, 'source', e.target.value)}
                              />
                            )}
                          </Flex>
                        </Flex>
                        {/* End Youtube Video Upload */}
                        <Flex gap='24px' align="center">
                          <Box flex={1}>
                            <Text fontWeight="medium" mb='8px'>Background Youtube Video Title</Text>
                            <Input
                              placeholder="What you want the user to see in the app"
                              flex={1}
                              value={findVideoDataByLanguage(lang)?.video?.title || ''}
                              onChange={e => {
                                updateVideoDataByLanguage(lang, 'title', e.target.value);
                              }}
                            />
                          </Box>
                          <Box width="120px">
                            <Text fontWeight="medium" mb='8px'>Duration</Text>
                            <Input
                              placeholder="hh:mm:ss"
                              width="120px"
                              value={findVideoDataByLanguage(lang)?.video?.duration || ''}
                              onChange={e => {
                                updateVideoDataByLanguage(lang, 'duration', e.target.value);
                              }}
                            />
                          </Box>
                        </Flex>
                      </Box>
                    </Tabs.Content>
                  ))}
                </Tabs.Root>
              </Box>
            </Box>
          </Dialog.Body>
        </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}; 